import type { MarketCode, Sport } from '@sharpr/types'
import type { StripeCancellationFeedback } from '@/types/stripeCancellation'
import { withPostHog } from './init'

// SOT for every PostHog event name + property shape. Add events here BEFORE wiring
// call sites so the compiler enforces complete, correctly-typed property bags.
// Conventions: names are verb_outcome; collapse multi-method outcomes via a `method`
// property (not separate names); engagement events carry `surface`; person-property
// writes live in `$set` / `$set_once`.

/** Signup shape for checkout_started / subscription_activated. Classified once by
 *  `signupPlanType` (services/billing/buildSignupSummary) so both funnel steps agree. */
export type SignupPlanType = 'trial' | 'presale' | 'season_tail' | 'paid_immediate'

export const EVENTS = {
  // Referral funnel
  REFERRAL_LINK_VISITED: 'referral_link_visited',

  // Auth
  SIGNUP_COMPLETED: 'signup_completed',
  LOGIN_SUBMITTED: 'login_submitted',
  LOGIN_SUCCEEDED: 'login_succeeded',
  LOGIN_FAILED: 'login_failed',
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  PASSWORD_RESET_EMAIL_SENT: 'password_reset_email_sent',
  PASSWORD_RESET_REQUEST_FAILED: 'password_reset_request_failed',
  PASSWORD_RESET_COMPLETED: 'password_reset_completed',
  PASSWORD_RESET_FAILED: 'password_reset_failed',

  // Billing
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_SETUP_FAILED: 'checkout_setup_failed',
  TRIAL_STARTED: 'trial_started',
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_ACTIVATED: 'subscription_activated',
  TRIAL_CONVERTED: 'trial_converted',
  SUBSCRIPTION_CANCEL_REQUESTED: 'subscription_cancel_requested',

  // Billing lifecycle (server-side, from Stripe webhooks + the season-billing cron)
  PAYMENT_FAILED: 'payment_failed',
  PAYMENT_RECOVERED: 'payment_recovered',
  SUBSCRIPTION_CANCEL_SCHEDULED: 'subscription_cancel_scheduled',
  SUBSCRIPTION_REACTIVATED: 'subscription_reactivated',
  SUBSCRIPTION_ENDED: 'subscription_ended',
  BILLING_RESUMED: 'billing_resumed',

  // Acquisition + upsell
  CTA_CLICKED: 'cta_clicked',
  UPGRADE_PROMPT_VIEWED: 'upgrade_prompt_viewed',
  UPGRADE_PROMPT_CLICKED: 'upgrade_prompt_clicked',

  // App engagement
  RESEARCH_OVERLAY_OPENED: 'research_overlay_opened',
  LINE_MOVEMENT_OPENED: 'line_movement_opened',
  PLAYER_SELECTED: 'player_selected',
  SELECT_ALL_CLICKED: 'select_all_clicked',
  CLEAR_ALL_CLICKED: 'clear_all_clicked',
  SLIP_SHARE_INITIATED: 'slip_share_initiated',
  SLIP_SHARED: 'slip_shared',
} as const

export type EventName = (typeof EVENTS)[keyof typeof EVENTS]

// Referral-attribution person-property writes, shipped on any referral_code event.
// attributed_referral_code = last-touch; first_referral_code_seen = first-touch ($set_once).
type ReferralPersonSet = {
  $set?: { attributed_referral_code: string }
  $set_once?: { first_referral_code_seen: string }
}

type ReferralProps = {
  referral_code?: string
} & ReferralPersonSet

// Shared engagement props — factored out so a new event can't omit sport/surface.
type SportProps = { sport: Sport }
type SurfaceProps = { surface: string }

/** Where a marketing CTA lives. Header CTAs fire on every page — $pathname disambiguates. */
export type CtaLocation =
  | 'hero'
  | 'final_cta'
  | 'demo_odds_slate'
  | 'demo_projections_slate'
  | 'header_sport'
  | 'header_sign_in'
  | 'header_start_trial'

/** Which upsell surface a prompt appeared on. */
export type UpgradePromptSurface =
  | 'table_projection' // desktop table: blurred projection popover
  | 'card_best_bet' // player card: blurred best-bet popover
  | 'book_line_ev' // per-book line: blurred EV popover
  | 'mobile_expansion_banner' // mobile card dropdown: "Start free trial to unlock EV"
  | 'sport_banner' // logged-in, no access to this sport: slim banner above the table

/** Entitlement of the viewer who saw the prompt. */
export type UpgradeViewer = 'anonymous' | 'logged_in_free' | 'subscriber_other_sport'

// Stripe setup_intent failure taxonomy. Union of common top-level error.codes AND
// decline_codes; narrowed to one canonical `reason` (decline_code preferred when
// error.code = 'card_declined') for clean dashboard breakdowns.
export type CheckoutFailureReason =
  // Top-level error.code values (rarely seen directly when decline_code exists)
  | 'card_declined'
  | 'processing_error'
  | 'authentication_required'
  | 'setup_intent_authentication_failure'
  | 'payment_method_provider_decline'
  | 'payment_method_provider_timeout'
  // decline_code values (preferred when error.code = 'card_declined')
  | 'insufficient_funds'
  | 'lost_card'
  | 'stolen_card'
  | 'expired_card'
  | 'incorrect_cvc'
  | 'incorrect_number'
  | 'do_not_honor'
  | 'generic_decline'
  | 'try_again_later' // deprecated decline code that Stripe still documents
  | 'card_velocity_exceeded'
  | 'fraudulent'
  | 'issuer_not_available'
  | 'invalid_account'
  | 'pickup_card'
  | 'unknown'

// SOT for the webhook arm + the type above. Verified against
// docs.stripe.com/error-codes + docs.stripe.com/declines/codes.
const KNOWN_CHECKOUT_FAILURE_REASONS = new Set<CheckoutFailureReason>([
  'card_declined',
  'processing_error',
  'authentication_required',
  'setup_intent_authentication_failure',
  'payment_method_provider_decline',
  'payment_method_provider_timeout',
  'insufficient_funds',
  'lost_card',
  'stolen_card',
  'expired_card',
  'incorrect_cvc',
  'incorrect_number',
  'do_not_honor',
  'generic_decline',
  'try_again_later',
  'card_velocity_exceeded',
  'fraudulent',
  'issuer_not_available',
  'invalid_account',
  'pickup_card',
])

/**
 * Narrow Stripe's open-ended error taxonomy to CheckoutFailureReason. Prefers
 * decline_code (the specific signal under error.code='card_declined'); unknown → 'unknown'.
 * Pass raw values from `setup_intent.last_setup_error` (nullable on non-card failures).
 */
export function narrowCheckoutFailureReason(
  errorCode: string | null | undefined,
  declineCode: string | null | undefined,
): CheckoutFailureReason {
  const effective =
    errorCode === 'card_declined' && declineCode ? declineCode : errorCode ?? ''
  return KNOWN_CHECKOUT_FAILURE_REASONS.has(effective as CheckoutFailureReason)
    ? (effective as CheckoutFailureReason)
    : 'unknown'
}

export type EventProperties = {
  [EVENTS.REFERRAL_LINK_VISITED]: {
    referral_code: string
    source_page: 'pricing' | 'signup'
    $set_once?: { first_referral_code_seen: string }
  }

  [EVENTS.SIGNUP_COMPLETED]:
    | ({
        method: 'email'
        has_share_id: boolean
      } & ReferralProps)
    | {
        method: 'google'
        provider: string
      }

  [EVENTS.LOGIN_SUBMITTED]: { method: 'email' | 'google' }
  [EVENTS.LOGIN_SUCCEEDED]: {
    method: 'email' | 'google'
    provider?: string
    redirect_to?: string
  }
  [EVENTS.LOGIN_FAILED]: {
    method: 'email' | 'google'
    reason: string
  }

  [EVENTS.PASSWORD_RESET_REQUESTED]: Record<string, never>
  [EVENTS.PASSWORD_RESET_EMAIL_SENT]: Record<string, never>
  [EVENTS.PASSWORD_RESET_REQUEST_FAILED]: { reason: string }
  [EVENTS.PASSWORD_RESET_COMPLETED]: Record<string, never>
  // reason = Supabase error.code ?? error.name, or 'link_invalid' (no recovery session on
  // load); message = the raw error message / link_invalid cause.
  [EVENTS.PASSWORD_RESET_FAILED]: { reason: string; message?: string }

  [EVENTS.CHECKOUT_STARTED]: {
    products: string[]
    sport_count: number
    sports: Sport[]
    plan_type: SignupPlanType
    has_share_id: boolean
  } & ReferralProps

  [EVENTS.CHECKOUT_SETUP_FAILED]: {
    reason: CheckoutFailureReason
    decline_code?: string
    error_code?: string
  }

  // cohort_intent_id = the checkout.session.id that initiated the cohort (joins events
  // to share_attributions / stripe_processed_events / Stripe). share_id = slip-share
  // attribution token from the originating `/s/<id>` URL.
  [EVENTS.TRIAL_STARTED]: {
    sports: Sport[]
    sport_count: number
    trial_days: number
    cohort_intent_id: string
    share_id?: string
  } & ReferralProps

  [EVENTS.SUBSCRIPTION_STARTED]: {
    sports: Sport[]
    sport_count: number
    cohort_intent_id: string
    share_id?: string
  } & ReferralProps

  // Unified activation event (trial + paid-immediate). Funnel step F; trial_started /
  // subscription_started remain as secondary events for trial-specific analyses.
  [EVENTS.SUBSCRIPTION_ACTIVATED]: {
    plan_type: SignupPlanType
    sports: Sport[]
    sport_count: number
    trial_days?: number
    cohort_intent_id: string
    share_id?: string
  } & ReferralProps

  [EVENTS.TRIAL_CONVERTED]: {
    sports: Sport[]
    sport_count: number
  } & ReferralProps

  [EVENTS.SUBSCRIPTION_CANCEL_REQUESTED]: {
    sport: Sport
    reason: StripeCancellationFeedback
    has_feedback: boolean
    period_end_unix: number | null
  }

  // --- Billing lifecycle (server) — one Stripe sub per sport, so each carries one sport.
  // Deduplicated per Stripe event/invoice via captureEventServer's dedupeKey.
  [EVENTS.PAYMENT_FAILED]: SportProps & { subscription_id: string }
  [EVENTS.PAYMENT_RECOVERED]: SportProps & { subscription_id: string }
  // Fires for every cancel path (in-app dialog AND Stripe Portal); the client-side
  // subscription_cancel_requested only sees the in-app one.
  [EVENTS.SUBSCRIPTION_CANCEL_SCHEDULED]: SportProps & {
    subscription_id: string
    access_end_unix: number | null
  }
  [EVENTS.SUBSCRIPTION_REACTIVATED]: SportProps & { subscription_id: string }
  // Sub actually ended (customer.subscription.deleted) — the churn event.
  [EVENTS.SUBSCRIPTION_ENDED]: SportProps & {
    subscription_id: string
    reason: string | null
    feedback: string | null
  }
  // Season resume: month-one invoice + unpause. payment='failed' = invoice declined (the
  // sub still unpauses; see CLAUDE.md "Declined month-one resume invoice").
  [EVENTS.BILLING_RESUMED]: SportProps & {
    subscription_id: string
    payment: 'paid' | 'failed'
    amount_cents: number
    season_start: string
  }

  // --- Acquisition + upsell (client)
  [EVENTS.CTA_CLICKED]: {
    location: CtaLocation
    label: string
    destination: string
  }
  [EVENTS.UPGRADE_PROMPT_VIEWED]: {
    surface: UpgradePromptSurface
    sport: Sport | null
    viewer: UpgradeViewer
  }
  [EVENTS.UPGRADE_PROMPT_CLICKED]: {
    surface: UpgradePromptSurface
    sport: Sport | null
    viewer: UpgradeViewer
    action: 'cta' | 'secondary'
    destination: string
  }

  [EVENTS.RESEARCH_OVERLAY_OPENED]: SportProps &
    SurfaceProps & {
      player_id: string
      player_name: string
      team: string
      market: MarketCode | null
    }

  [EVENTS.LINE_MOVEMENT_OPENED]: SportProps &
    SurfaceProps & {
      player_id: string | null
      player_name: string
      team: string | null
      market: string
      book: string | null
      best_ev: number | null
      best_line: number | null
      best_side: string | null
    }

  [EVENTS.PLAYER_SELECTED]: SportProps &
    SurfaceProps & {
      action: 'add' | 'remove'
      player_id: string
      player_name: string
      team: string
      best_market?: MarketCode
      best_ev: number | null
    }

  [EVENTS.SELECT_ALL_CLICKED]: SportProps & SurfaceProps & { count: number }
  [EVENTS.CLEAR_ALL_CLICKED]: SportProps & SurfaceProps & { count_before: number }

  [EVENTS.SLIP_SHARE_INITIATED]: SportProps & {
    player_id: string
    player_name: string
    market: MarketCode
    line: number
    side: 'over' | 'under'
    book: string
    odds: number
    ev: number | null
    share_id: string | null
  }

  [EVENTS.SLIP_SHARED]: SportProps & {
    player_id: string
    player_name: string
    market: MarketCode
    line: number
    side: 'over' | 'under'
    book: string
    odds: number
    ev: number | null
    share_id: string | null
    destination: 'native_share' | 'clipboard'
  }
}

/**
 * Typed client-side event capture. All call sites go through this (ESLint-enforced over
 * raw `posthog.capture`). SDK load + init are lazy (lib/posthog/init.ts) — events fired
 * before the SDK finishes loading queue FIFO and flush on init.
 */
export function captureEvent<E extends EventName>(event: E, properties: EventProperties[E]): void {
  withPostHog((ph) => ph.capture(event, properties as Record<string, unknown>))
}

// captureEventServer lives in `./server` so client components importing this registry don't
// pull in posthog-node + node:fs. Server: `import { captureEventServer } from '@/lib/posthog/server'`.
